import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '@rb-serve-app/core';

interface SchumerBoxRow {
  title: string;
  value: string;
  description: string;
}

interface DisclosureSummaryProps {
  selectedOption: any;
  transaction: any;
  onScrollComplete: (isComplete: boolean) => void;
  schumerBoxData?: SchumerBoxRow[];
  schumerBoxLabel?: string;
  scrollToBottomLabel?: string;
}

const DisclosureSummary: React.FC<DisclosureSummaryProps> = ({
  selectedOption,
  transaction,
  onScrollComplete,
  schumerBoxData,
  schumerBoxLabel,
  scrollToBottomLabel,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isScrolledToBottom =
        Math.abs(scrollHeight - clientHeight - scrollTop) < 10;

      if (isScrolledToBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
        onScrollComplete(true);
      }
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      // Check if content is scrollable
      if (scrollElement.scrollHeight <= scrollElement.clientHeight) {
        // Content fits in container, consider it as scrolled
        setHasScrolledToBottom(true);
        onScrollComplete(true);
      } else {
        scrollElement.addEventListener('scroll', handleScroll);
        return () => scrollElement.removeEventListener('scroll', handleScroll);
      }
    }
  }, [hasScrolledToBottom, onScrollComplete]);

  // Use provided schumerBoxData or empty array
  const data = schumerBoxData || [];

  return (
    <div className="w-full">
      <div
        ref={scrollRef}
        className="h-[350px] overflow-y-auto border border-black scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-400"
        style={{ scrollBehavior: 'smooth' }}
      >
        {/* Desktop/Tablet Layout */}
        <div className="hidden md:block">
          <table
            className="w-full text-black"
            style={{ borderCollapse: 'collapse' }}
          >
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '50%' }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  colSpan={3}
                  className="bg-black text-white p-4 text-left font-['Ted_Next'] text-[16px] font-semibold border border-black"
                >
                  {schumerBoxLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  <td className="py-3 px-2 text-left font-medium border border-black">
                    {row.title}
                  </td>
                  <td className="py-3 px-6 text-center border border-black">
                    {row.value}
                  </td>
                  <td className="py-3 px-2 text-left border border-black">
                    {row.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <table
            className="w-full text-black"
            style={{ borderCollapse: 'collapse' }}
          >
            <colgroup>
              <col style={{ width: '50%' }} />
              <col style={{ width: '50%' }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  colSpan={2}
                  className="bg-black text-white p-4 text-left font-['Ted_Next'] text-[16px] font-semibold border border-black"
                >
                  {schumerBoxLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  <td className="py-3 px-2 text-left font-medium border border-black align-top">
                    {row.title}
                    <br />
                    <div className="font-semibold mb-1 mt-2">{row.value}</div>
                  </td>
                  <td className="py-3 px-2 text-left text-sm border border-black">
                    {row.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!hasScrolledToBottom && (
        <p className="text-sm text-gray-600 mt-2">{scrollToBottomLabel}</p>
      )}
    </div>
  );
};

export default DisclosureSummary;
